import React, { useState, useEffect } from 'react';
import LoadError from '../../components/LoadError';
import { profileTypeLabel } from '../../lib/utils';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../../contexts/AuthContext';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ArrowLeft, GraduationCap, BookOpen, Award } from 'lucide-react';
import { toast } from 'sonner';
import CourseCombobox from '../../components/CourseCombobox';

const ENGLISH_LABELS = {
  IELTS:    ['Listening', 'Reading', 'Writing', 'Speaking'],
  PTE:      ['Listening', 'Reading', 'Writing', 'Speaking'],
  TOEFL:    ['Listening', 'Reading', 'Writing', 'Speaking'],
  Duolingo: ['Overall', 'Literacy', 'Conversation', 'Comprehension'],
};

const EMPTY_FORM = {
  profile_type: 'masters',
  name: '', email: '', phone: '',
  semester_intake_year: '', course_interested: '', country: '',
  tenth_completion_year: '', tenth_percentage: '',
  inter_course_year: '', inter_percentage: '', inter_english_percentage: '',
  total_backlogs: '', english_test_type: '', english_test_score: '',
  english_listening: '', english_reading: '', english_writing: '', english_speaking: '',
  bachelor_course_name: '', bachelor_completion_year: '', bachelor_percentage: '',
  masters_course_name: '', masters_completion_year: '', masters_percentage: '',
  nationality: '', passport_number: '', notes: '',
  // School scores default to Percentage, degree scores to CGPA; each can be switched.
  tenth_score_type: 'percentage', inter_score_type: 'percentage',
  bachelor_score_type: 'cgpa', masters_score_type: 'cgpa',
};

// score field -> its Percentage/CGPA field
const SCORE_FIELDS = {
  tenth_percentage: 'tenth_score_type',
  inter_percentage: 'inter_score_type',
  bachelor_percentage: 'bachelor_score_type',
  masters_percentage: 'masters_score_type',
};
// Older candidates have no stored type: read it off the value ("7.5 CGPA", "8.2" -> CGPA).
const guessScoreType = (v) => {
  const n = parseFloat(String(v || '').replace(/[^\d.]/g, ''));
  return /cgpa|gpa/i.test(v || '') || (!/%/.test(v || '') && n > 0 && n <= 10) ? 'cgpa' : 'percentage';
};
// Returns an error message, or '' when the score is fine for its type.
const scoreError = (value, type) => {
  const num = String(value).replace(/%|cgpa/gi, '').trim();
  if (!/^\d+(\.\d+)?$/.test(num)) return 'must be a number';
  const max = type === 'cgpa' ? 10 : 100;
  return Number(num) > max ? `must be between 0 and ${max}` : '';
};

function ScoreInput({ label, value, type, onValue, onType, testId }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={testId}>{label}</Label>
        <div className="flex rounded-md border border-contrast/10 overflow-hidden text-[11px]" role="group" aria-label={`${label} format`}>
          {[['percentage', 'Percentage'], ['cgpa', 'CGPA']].map(([v, text]) => (
            <button key={v} type="button" onClick={() => onType(v)} aria-pressed={type === v}
              className={`px-2 py-0.5 ${type === v ? 'bg-brand text-white' : 'text-ink-2 hover:bg-contrast/[0.07]'}`}>
              {text}
            </button>
          ))}
        </div>
      </div>
      <Input id={testId} value={value} onChange={(e) => onValue(e.target.value)} inputMode="decimal"
        placeholder={type === 'cgpa' ? 'e.g. 8.5 (out of 10)' : 'e.g. 85 (%)'} data-testid={testId} />
    </div>
  );
}

// Validation helpers
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[\d\s\-]{7,15}$/;
const PASSPORT_RE = /^[A-Za-z0-9]{6,12}$/;
const YEAR_RE = /^(19|20)\d{2}$/;

export default function CandidateForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  // In edit mode a failed load must not show an empty form: saving it would blank the student's record.
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [original, setOriginal] = useState(EMPTY_FORM);
  const [form, setForm] = useState(() => {
    const base = { ...EMPTY_FORM };
    const country = searchParams.get('country');
    if (country) base.country = country;
    return base;
  });

  useEffect(() => {
    apiGet('/universities/countries').then(r => setCountries(r.data)).catch(console.error);
    if (isEdit) {
      setLoading(true);
      setLoadError(false);
      apiGet(`/candidates/${id}`).then(r => {
        const c = r.data;
        const updated = { ...EMPTY_FORM };
        Object.keys(updated).forEach(k => { if (c[k] !== undefined && c[k] !== null) updated[k] = c[k]; });
        Object.entries(SCORE_FIELDS).forEach(([f, t]) => { if (!c[t]) updated[t] = guessScoreType(c[f]); });
        setForm(updated);
        setOriginal(updated);
      }).catch(() => setLoadError(true)).finally(() => setLoading(false));
    }
  }, [id, isEdit, attempt]);

  const h = (name, value) => setForm(prev => ({ ...prev, [name]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Student name is required'); return; }
    if (!form.email) { toast.error('Email is required'); return; }
    if (!form.phone) { toast.error('Phone / Contact is required'); return; }
    if (!EMAIL_RE.test(form.email)) { toast.error('Please enter a valid email address'); return; }
    if (!PHONE_RE.test(form.phone)) { toast.error('Please enter a valid phone number (7–15 digits)'); return; }
    if (form.passport_number && !PASSPORT_RE.test(form.passport_number)) {
      toast.error('Passport number must be 6–12 alphanumeric characters'); return;
    }
    const yearFields = ['tenth_completion_year', 'inter_course_year', 'bachelor_completion_year', 'masters_completion_year'];
    for (const f of yearFields) {
      if (form[f] && !YEAR_RE.test(form[f].replace(/\D.*/, '').trim().slice(0, 4))) {
        // allow values like "MPC 2020" — extract just the 4-digit year part
        const yearPart = form[f].match(/\b(19|20)\d{2}\b/);
        if (!yearPart) { toast.error(`Please enter a valid year (e.g. 2024) for ${f.replace(/_/g, ' ')}`); return; }
      }
    }
    // Only scores entered or changed now are checked, so older free-text values never block a save.
    const scoreLabels = { tenth_percentage: '10th score', inter_percentage: '12th / Intermediate score', bachelor_percentage: "Bachelor's score", masters_percentage: "Master's score" };
    for (const [f, t] of Object.entries(SCORE_FIELDS)) {
      if (form[f] && (form[f] !== original[f] || form[t] !== original[t])) {
        const err = scoreError(form[f], form[t]);
        if (err) { toast.error(`${scoreLabels[f]} ${err} (${form[t] === 'cgpa' ? 'CGPA' : 'Percentage'})`); return; }
      }
    }
    if (form.total_backlogs && (isNaN(Number(form.total_backlogs)) || Number(form.total_backlogs) < 0)) {
      toast.error('Total backlogs must be a non-negative number'); return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await apiPut(`/candidates/${id}`, form);
        toast.success('Candidate updated');
        navigate(`/partner/candidates/${id}`);
      } else {
        const { data } = await apiPost('/candidates', form);
        toast.success('Candidate created');
        // Land straight in the "Add University Application" flow on the new
        // candidate's page, rather than just the bare profile — registering
        // a brand-new candidate is normally followed by applying them to a
        // university right away.
        navigate(`/partner/candidates/${data.id}`, { state: { openApply: true } });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  // "Register Candidate" only ever creates a brand-new candidate — the
  // backend rejects it (409) if the passport number is already on file,
  // which is the one and only duplicate rule it enforces. "Apply" is the
  // other half of that same rule: it's for a candidate who's already
  // registered (that passport number already exists) and just needs
  // another university application, so it looks them up by passport and
  // sends the partner straight to that candidate's page instead of
  // re-registering them.
  const handleApply = async () => {
    const passport = (form.passport_number || '').trim();
    if (!passport) { toast.error("Enter the candidate's passport number to find their profile"); return; }
    setApplying(true);
    try {
      const { data } = await apiGet(`/candidates?search=${encodeURIComponent(passport)}`);
      const match = (data || []).find(c => (c.passport_number || '').trim().toLowerCase() === passport.toLowerCase());
      if (match) {
        toast.success(`Found ${match.name} — opening their profile to add a university application`);
        navigate(`/partner/candidates/${match.id}`, { state: { openApply: true } });
      } else {
        toast.error('No candidate with this passport number found in your list. Use "Register Candidate" to add them as new.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    } finally { setApplying(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" /></div>;
  if (loadError) return <LoadError what="this student's details" onRetry={() => setAttempt(a => a + 1)} />;

  const showBachelor = form.profile_type === 'masters' || form.profile_type === 'phd';
  const showMasters = form.profile_type === 'phd';

  const courseLabel = profileTypeLabel(form.profile_type);

  const subScoreLabels = form.english_test_type ? ENGLISH_LABELS[form.english_test_type] : null;

  return (
    <div className="max-w-3xl mx-auto animate-fade-in-up" data-testid="candidate-form-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-ink-2 hover:text-ink mb-6 text-sm" data-testid="back-button">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="bg-surface rounded-xl border border-contrast/[0.07] shadow-card p-8">
        <h1 className="text-2xl font-semibold text-ink mb-6" style={{ fontFamily: 'Outfit' }}>
          {isEdit ? 'Edit Candidate' : 'Register New Candidate'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Type */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-ink-3 mb-4">Student Profile Type</h3>
            <Tabs value={form.profile_type} onValueChange={(v) => h('profile_type', v)} className="w-full">
              <TabsList className="grid w-full grid-cols-3 h-12 bg-contrast/[0.07]" data-testid="profile-type-tabs">
                <TabsTrigger value="phd" className="gap-2 text-sm data-[state=active]:bg-iris data-[state=active]:text-white" data-testid="phd-tab">
                  <Award className="w-4 h-4" /> PhD
                </TabsTrigger>
                <TabsTrigger value="masters" className="gap-2 text-sm data-[state=active]:bg-brand data-[state=active]:text-white" data-testid="masters-tab">
                  <GraduationCap className="w-4 h-4" /> Master's
                </TabsTrigger>
                <TabsTrigger value="bachelors" className="gap-2 text-sm data-[state=active]:bg-aqua data-[state=active]:text-white" data-testid="bachelors-tab">
                  <BookOpen className="w-4 h-4" /> Bachelor's
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Student Info */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-ink-3 mb-4">Student Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="cf-name">Name of the Student <span className="text-ember">*</span></Label>
                <Input id="cf-name" value={form.name} onChange={(e) => h('name', e.target.value)} placeholder="Full name" data-testid="candidate-name" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-email">Email <span className="text-ember">*</span></Label>
                <Input id="cf-email" type="email" value={form.email} onChange={(e) => h('email', e.target.value)} placeholder="student@email.com" data-testid="candidate-email" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-phone">Phone / Contact <span className="text-ember">*</span></Label>
                <Input id="cf-phone" value={form.phone} onChange={(e) => h('phone', e.target.value)} placeholder="+91 9876543210" data-testid="candidate-phone" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-semester_intake_year">Semester / Intake</Label>
                <Input id="cf-semester_intake_year" value={form.semester_intake_year} onChange={(e) => h('semester_intake_year', e.target.value)} placeholder={`e.g. September ${new Date().getFullYear()}`} data-testid="candidate-semester-intake" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="candidate-course-interested">Course Interested in {courseLabel}</Label>
                <CourseCombobox
                  value={form.course_interested}
                  onChange={(v) => h('course_interested', v)}
                  params={{ country: form.country, level: form.profile_type }}
                  placeholder={form.country ? 'Select course...' : 'Select course (pick a country to narrow the list)'}
                  testId="candidate-course-interested"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-candidate-country">Country Interested</Label>
                <Select value={form.country} onValueChange={(v) => h('country', v)}>
                  <SelectTrigger id="cf-candidate-country" data-testid="candidate-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                  <SelectContent>
                    {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-nationality">Nationality</Label>
                <Input id="cf-nationality" value={form.nationality} onChange={(e) => h('nationality', e.target.value)} placeholder="e.g. Indian" data-testid="candidate-nationality" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-passport_number">Passport Number</Label>
                <Input id="cf-passport_number" value={form.passport_number} onChange={(e) => h('passport_number', e.target.value)} placeholder="e.g. A1234567" data-testid="candidate-passport" />
              </div>
            </div>
          </div>

          {/* Academic Details */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-ink-3 mb-4">Academic Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 10th */}
              <div className="space-y-2">
                <Label htmlFor="cf-tenth_completion_year">10th Completion Year</Label>
                <Input id="cf-tenth_completion_year" value={form.tenth_completion_year} onChange={(e) => h('tenth_completion_year', e.target.value)} placeholder="e.g. 2018" data-testid="candidate-tenth-year" />
              </div>
              <ScoreInput label="10th Score" value={form.tenth_percentage} type={form.tenth_score_type}
                onValue={(v) => h('tenth_percentage', v)} onType={(v) => h('tenth_score_type', v)} testId="candidate-tenth-percentage" />

              {/* 12th / Intermediate */}
              <div className="space-y-2">
                <Label htmlFor="cf-inter_course_year">12th / Intermediate Course & Year</Label>
                <Input id="cf-inter_course_year" value={form.inter_course_year} onChange={(e) => h('inter_course_year', e.target.value)} placeholder="e.g. MPC 2020" data-testid="candidate-inter-course-year" />
              </div>
              <ScoreInput label="12th / Intermediate Score" value={form.inter_percentage} type={form.inter_score_type}
                onValue={(v) => h('inter_percentage', v)} onType={(v) => h('inter_score_type', v)} testId="candidate-inter-percentage" />
              <div className="space-y-2">
                <Label htmlFor="cf-inter_english_percentage">12th / Intermediate English %</Label>
                <Input id="cf-inter_english_percentage" value={form.inter_english_percentage} onChange={(e) => h('inter_english_percentage', e.target.value)} placeholder="e.g. 78%" data-testid="candidate-inter-english" />
              </div>

              {/* Bachelor - for Masters & PhD */}
              {showBachelor && (
                <>
                  <div className="md:col-span-2 mt-2">
                    <div className="border-t border-contrast/10 pt-4">
                      <span className="text-xs font-bold uppercase tracking-[0.1em] text-brand">Bachelor's Details</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cf-bachelor_course_name">Bachelor Course Name</Label>
                    <Input id="cf-bachelor_course_name" value={form.bachelor_course_name} onChange={(e) => h('bachelor_course_name', e.target.value)} placeholder="e.g. B.Tech CSE" data-testid="candidate-bachelor-course" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cf-bachelor_completion_year">Bachelors Completion Year</Label>
                    <Input id="cf-bachelor_completion_year" value={form.bachelor_completion_year} onChange={(e) => h('bachelor_completion_year', e.target.value)} placeholder="e.g. 2024" data-testid="candidate-bachelor-year" />
                  </div>
                  <ScoreInput label="Bachelor's Score" value={form.bachelor_percentage} type={form.bachelor_score_type}
                    onValue={(v) => h('bachelor_percentage', v)} onType={(v) => h('bachelor_score_type', v)} testId="candidate-bachelor-percentage" />
                  <div className="space-y-2">
                    <Label htmlFor="cf-total_backlogs">Total No of Backlogs</Label>
                    <Input id="cf-total_backlogs" value={form.total_backlogs} onChange={(e) => h('total_backlogs', e.target.value)} placeholder="e.g. 0" data-testid="candidate-backlogs" />
                  </div>
                </>
              )}

              {/* Backlogs for bachelors profile (no bachelor's details section) */}
              {!showBachelor && (
                <div className="space-y-2">
                  <Label htmlFor="cf-total_backlogs">Total No of Backlogs</Label>
                  <Input id="cf-total_backlogs" value={form.total_backlogs} onChange={(e) => h('total_backlogs', e.target.value)} placeholder="e.g. 0" data-testid="candidate-backlogs" />
                </div>
              )}

              {/* Masters - for PhD only */}
              {showMasters && (
                <>
                  <div className="md:col-span-2 mt-2">
                    <div className="border-t border-contrast/10 pt-4">
                      <span className="text-xs font-bold uppercase tracking-[0.1em] text-iris">Master's Details</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cf-masters_course_name">Masters Course Name</Label>
                    <Input id="cf-masters_course_name" value={form.masters_course_name} onChange={(e) => h('masters_course_name', e.target.value)} placeholder="e.g. M.Tech CSE" data-testid="candidate-masters-course" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cf-masters_completion_year">Masters Completion Year</Label>
                    <Input id="cf-masters_completion_year" value={form.masters_completion_year} onChange={(e) => h('masters_completion_year', e.target.value)} placeholder="e.g. 2026" data-testid="candidate-masters-year" />
                  </div>
                  <ScoreInput label="Master's Score" value={form.masters_percentage} type={form.masters_score_type}
                    onValue={(v) => h('masters_percentage', v)} onType={(v) => h('masters_score_type', v)} testId="candidate-masters-percentage" />
                </>
              )}

              {/* English Language Score */}
              <div className="md:col-span-2 mt-2">
                <div className="border-t border-contrast/10 pt-4">
                  <span className="text-xs font-bold uppercase tracking-[0.1em] text-aqua">English Language Score</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-candidate-english-test-type">Test Type</Label>
                <Select value={form.english_test_type} onValueChange={(v) => h('english_test_type', v)}>
                  <SelectTrigger id="cf-candidate-english-test-type" data-testid="candidate-english-test-type"><SelectValue placeholder="Select test" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IELTS">IELTS</SelectItem>
                    <SelectItem value="PTE">PTE</SelectItem>
                    <SelectItem value="TOEFL">TOEFL</SelectItem>
                    <SelectItem value="Duolingo">Duolingo</SelectItem>
                    <SelectItem value="MOI">MOI</SelectItem>
                    <SelectItem value="Yet to take">Yet to take</SelectItem>
                    <SelectItem value="NA">NA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-english_test_score">Overall Score</Label>
                <Input id="cf-english_test_score" value={form.english_test_score} onChange={(e) => h('english_test_score', e.target.value)} placeholder="e.g. 7.0 / 110 / 65" data-testid="candidate-english-test-score" />
              </div>
              {subScoreLabels && subScoreLabels.map((label, idx) => {
                const fieldKey = ['english_listening', 'english_reading', 'english_writing', 'english_speaking'][idx];
                return (
                  <div key={fieldKey} className="space-y-2">
                    <Label htmlFor={`cf-${fieldKey}`}>{label}</Label>
                    <Input id={`cf-${fieldKey}`} value={form[fieldKey]} onChange={(e) => h(fieldKey, e.target.value)} placeholder={`${label} score`} data-testid={`candidate-${fieldKey}`} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="cf-notes">Additional Notes</Label>
            <Textarea id="cf-notes" value={form.notes} onChange={(e) => h('notes', e.target.value)} data-testid="candidate-notes" rows={3} placeholder="Any additional information..." />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-contrast/10">
            <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="cancel-btn">Cancel</Button>
            {!isEdit && (
              <Button
                type="button"
                variant="outline"
                onClick={handleApply}
                disabled={applying}
                data-testid="apply-candidate-btn"
                className="border-brand text-brand hover:bg-brand/10"
              >
                {applying ? 'Searching...' : 'Apply'}
              </Button>
            )}
            <Button type="submit" disabled={saving} data-testid="save-candidate-btn" className="bg-brand hover:bg-brand/90 text-white">
              {saving ? 'Saving...' : isEdit ? 'Update Candidate' : 'Register Candidate'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}


